import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom/client';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  CalculatorOutlined,
  CheckOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  LockOutlined,
  PrinterOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import { PayrollPayslipSheet } from '@/components/hr/PayrollPayslipSheet';
import { PayrollLabourCardSheet } from '@/components/hr/PayrollLabourCardSheet';
import { ENTITY_TYPE_HR_PAYROLL_PERIOD } from '@/constants/approval-entity-types';
import hrmsService, {
  hrmsKeys,
  PAYROLL_RUN_SCOPE_LABEL,
  type PayrollGlLine,
  type PayrollRegisterRow,
  type PayrollRunLine,
} from '@/services/hrms.service';
import { formatMoney, PRINT_PLAIN_AMOUNT_PROFILE } from '@/utils/format/money';
import { PAYROLL_STATUS_COLOR, buildLinearWorkflow, hrmsStatusColor, showApprovalPanel } from './shared/hrmsStatus';
import { getOrFindFromList } from './shared/hrmsFetch';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import { useHrmsPolicy } from '@/hooks/useHrmsPolicy';

const { Text } = Typography;

const amt = (n: unknown) => formatMoney(n, PRINT_PLAIN_AMOUNT_PROFILE);

const GL_ROLE_LABEL: Record<PayrollGlLine['account_role'], string> = {
  salary_expense: 'Salary expense',
  salary_deduction: 'Salary deduction',
  employee_loan: 'Employee loan',
  salary_payable: 'Salaries payable',
};

function lineDetail(l: PayrollRunLine): string {
  if (l.hours) return `${l.hours} h × ${amt(l.rate)}`;
  if (l.days) return `${l.days} day(s) × ${amt(l.rate)}`;
  if (l.monthly && Math.abs(l.monthly - l.amount) > 0.005) return `of ${amt(l.monthly)} monthly`;
  return '';
}

function openPrintOverlay(render: (onClose: () => void) => React.ReactElement) {
  const main = document.getElementById('root');
  const el = document.createElement('div');
  el.id = 'print-container';
  document.body.appendChild(el);
  if (main) main.style.display = 'none';
  const root = ReactDOM.createRoot(el);
  const cleanup = () => {
    root.unmount();
    el.parentNode?.removeChild(el);
    if (main) main.style.display = 'block';
    window.removeEventListener('afterprint', cleanup);
  };
  root.render(render(cleanup));
  window.addEventListener('afterprint', cleanup);
}

const PayrollPeriodDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const { indiaPack } = useHrmsPolicy();
  const { data: departments = [] } = useDepartmentsForSelection();
  const [action, setAction] = useState<string | null>(null);
  const [payForm] = Form.useForm();

  const { data: period, isLoading } = useQuery({
    queryKey: hrmsKeys.payrollPeriod(id || ''),
    queryFn: () =>
      getOrFindFromList(
        () => hrmsService.getPayrollPeriod(id!),
        () => hrmsService.listPayrollPeriods(),
        id!
      ),
    enabled: Boolean(id),
  });

  const { data: payments = [], isLoading: payLoading } = useQuery({
    queryKey: hrmsKeys.payrollPayments(id),
    queryFn: () => hrmsService.listPayrollPayments({ payroll_period_id: id }),
    enabled: Boolean(id),
  });

  const { data: register, isLoading: registerLoading } = useQuery({
    queryKey: [...hrmsKeys.payrollPeriod(id || ''), 'register'],
    queryFn: () => hrmsService.getPayrollRegister(id!),
    enabled: Boolean(id),
  });
  const rows = register?.rows ?? [];

  const workflow = useMemo(
    () =>
      buildLinearWorkflow(['Draft', 'Calculated', 'Pending approval', 'Approved', 'Locked'], period?.status || 'Draft'),
    [period?.status]
  );

  const invalidate = async () => {
    await queryClient.invalidateQueries({
      queryKey: hrmsKeys.payrollPeriod(id!),
    });
    await queryClient.invalidateQueries({
      queryKey: hrmsKeys.payrollPeriods(),
    });
    await queryClient.invalidateQueries({
      queryKey: hrmsKeys.payrollPayments(id),
    });
  };

  const run = async (key: string, fn: () => Promise<unknown>, ok: string) => {
    setAction(key);
    try {
      await fn();
      message.success(ok);
      await invalidate();
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || `${ok} failed`);
    } finally {
      setAction(null);
    }
  };

  const periodLabel = period?.period_year
    ? `${String(period.period_month).padStart(2, '0')}/${period.period_year}`
    : period?.period_name || id;

  if (isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Payroll period" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }
  if (!period) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Payroll period" />}>
        <Card>Not found</Card>
      </WorkspaceLayout>
    );
  }

  const createPayment = async () => {
    try {
      const v = await payForm.validateFields();
      await hrmsService.createPayrollPayment({
        ...v,
        payroll_period_id: id,
        payment_date: v.payment_date.format('YYYY-MM-DD'),
      });
      message.success('Payment batch created');
      payForm.resetFields();
      await invalidate();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || 'Payment create failed');
    }
  };

  const deletePayment = (r: { id: string; payment_batch_number: string }) =>
    modal.confirm({
      title: `Delete payment batch ${r.payment_batch_number}?`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await hrmsService.deletePayrollPayment(r.id);
          message.success('Payment batch deleted');
          await invalidate();
        } catch (e: any) {
          message.error(e?.response?.data?.message || e?.message || 'Delete failed');
        }
      },
    });

  const batchedAmount = payments.reduce((s: number, p: any) => s + (Number(p.total_amount) || 0), 0);
  const unpaidBalance = Math.round(((Number(period.total_net) || 0) - batchedAmount) * 100) / 100;
  const canCreateBatch = ['Approved', 'Locked', 'Paid'].includes(period.status) && unpaidBalance > 0;

  const exportWps = async () => {
    setAction('wps');
    try {
      const meta = await hrmsService.downloadWpsExport(id!, `wps-${periodLabel}.sif`);
      if (meta.skippedCount > 0) {
        message.warning(
          `WPS exported (${meta.included} rows). Skipped ${meta.skippedCount}: ${meta.skippedDetail || 'see employee IBANs'}`
        );
      } else {
        message.success(`WPS exported (${meta.included || 'all'} rows)`);
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'WPS export failed');
    } finally {
      setAction(null);
    }
  };

  const exportNeft = async () => {
    setAction('neft');
    try {
      const meta = await hrmsService.downloadNeftExport(id!, `neft-${periodLabel}.csv`);
      if (meta.skippedCount > 0) {
        message.warning(
          `NEFT exported (${meta.included} rows). Skipped ${meta.skippedCount}: ${meta.skippedDetail || 'see account/IFSC'}`
        );
      } else {
        message.success(`NEFT exported (${meta.included || 'all'} rows)`);
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'NEFT export failed');
    } finally {
      setAction(null);
    }
  };

  const canCompute = !['Pending approval', 'Approved', 'Locked', 'Paid'].includes(period.status);
  const canDelete = ['Draft', 'Calculated', 'Rejected'].includes(period.status) && payments.length === 0;
  const runScopeLabel =
    PAYROLL_RUN_SCOPE_LABEL[period.run_scope as keyof typeof PAYROLL_RUN_SCOPE_LABEL] || period.run_scope;
  const notInRun: Array<{
    employee_id: string;
    employee_code: string;
    full_name: string;
    timesheet_status: string;
  }> = Array.isArray(period.project_distribution_json?.not_in_run) ? period.project_distribution_json.not_in_run : [];
  const inOtherRuns = Number(period.project_distribution_json?.in_other_runs || 0);
  const timesheetsLink = `/hr/attendance?tab=months&month=${period.period_year}-${String(period.period_month).padStart(2, '0')}`;

  const deleteRun = () =>
    modal.confirm({
      title: `Delete payroll run ${period.period_number}?`,
      content: 'Its computed salary lines are removed. The employees become available for another run this month.',
      okText: 'Delete run',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await hrmsService.deletePayrollPeriod(id!);
          message.success('Payroll run deleted');
          await queryClient.invalidateQueries({
            queryKey: hrmsKeys.payrollPeriods(),
          });
          navigate('/hr/payroll');
        } catch (e: any) {
          message.error(e?.response?.data?.message || e?.message || 'Delete failed');
        }
      },
    });
  const withoutTimesheet: Array<{
    employee_id: string;
    employee_code: string;
    full_name: string;
    timesheet_status: string;
    net: number;
  }> = Array.isArray(period.project_distribution_json?.without_approved_timesheet)
    ? period.project_distribution_json.without_approved_timesheet
    : [];

  const glHandoff = async () => {
    setAction('gl');
    try {
      const r = await hrmsService.payrollGlHandoff(id!);
      if (r.journal_voucher_number && !r.issues?.length) {
        message.success(`Draft JV ${r.journal_voucher_number} created — review and post it in Finance`);
      } else {
        modal.warning({
          title: 'Finance JV not created',
          content: (
            <Space direction="vertical" size={4}>
              <Text>{r.message}</Text>
              {(r.issues || []).map((i) => (
                <Text key={i} type="secondary">
                  • {i}
                </Text>
              ))}
            </Space>
          ),
        });
      }
      await invalidate();
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Finance JV failed');
    } finally {
      setAction(null);
    }
  };

  const markPaid = async (paymentId: string) => {
    setAction(`paid-${paymentId}`);
    try {
      await hrmsService.markPayrollPaymentPaid(paymentId);
      message.success('Payment marked paid');
      await invalidate();
    } catch {
      message.error('Mark paid failed');
    } finally {
      setAction(null);
    }
  };

  const printPayslips = async (employeeId?: string) => {
    setAction(employeeId ? `payslip-${employeeId}` : 'payslip-all');
    try {
      const doc = await hrmsService.getPayrollPayslips(id!, employeeId);
      if (!doc.slips.length) {
        message.info('No computed employees in this run');
        return;
      }
      openPrintOverlay((onClose) => <PayrollPayslipSheet doc={doc} onClose={onClose} />);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Payslip load failed');
    } finally {
      setAction(null);
    }
  };

  const printLabourCards = async (employeeId?: string) => {
    setAction(employeeId ? `labour-${employeeId}` : 'labour-all');
    try {
      const cards = await hrmsService.getPayrollLabourCards(id!, employeeId);
      if (!cards.length) {
        message.info('No computed employees in this run');
        return;
      }
      openPrintOverlay((onClose) => <PayrollLabourCardSheet cards={cards} onClose={onClose} />);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Labour card load failed');
    } finally {
      setAction(null);
    }
  };

  const exportRegister = async () => {
    setAction('register');
    try {
      await hrmsService.downloadPayrollRegister(id!, `salary-register-${period.period_number}.xlsx`);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Register export failed');
    } finally {
      setAction(null);
    }
  };

  const showRunCoverage = ['Draft', 'Calculated', 'Rejected'].includes(period.status);
  const gl = register?.gl;
  const flaggedCount = rows.filter((r) => r.flags.length > 0).length;
  const componentColumns = (register?.columns ?? []).map((c) => {
    const key = `${c.type}:${c.code}`;
    return {
      title: c.type === 'Deduction' ? `${c.name} (−)` : c.name,
      key,
      width: 110,
      align: 'right' as const,
      render: (_: unknown, r: PayrollRegisterRow) => (r.amounts[key] ? amt(r.amounts[key]) : ''),
    };
  });

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Payroll run"
          documentNumber={String(period.period_number || periodLabel)}
          statusTag={<Tag color={hrmsStatusColor(period.status, PAYROLL_STATUS_COLOR)}>{period.status}</Tag>}
          subtitle={
            <Text type="secondary">
              {periodLabel} · Run {period.run_number} · {runScopeLabel} · {period.employee_count ?? 0} employees
            </Text>
          }
          primaryActions={
            <Space wrap>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/payroll')}>
                Back
              </Button>
              {canCompute && (
                <Button
                  icon={<CalculatorOutlined />}
                  type={period.status === 'Draft' || period.status === 'Rejected' ? 'primary' : 'default'}
                  loading={action === 'compute'}
                  onClick={() => run('compute', () => hrmsService.computePayrollPeriod(id!), 'Computed')}
                >
                  {period.status === 'Draft' ? 'Compute' : 'Recompute'}
                </Button>
              )}
              {period.status === 'Calculated' && (
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  loading={action === 'submit'}
                  onClick={() => run('submit', () => hrmsService.submitPayrollPeriod(id!), 'Submitted for approval')}
                >
                  Submit for approval
                </Button>
              )}
              {period.status === 'Pending approval' && !period.approval_instance_id && (
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  loading={action === 'approve'}
                  onClick={() => run('approve', () => hrmsService.approvePayrollPeriod(id!), 'Approved')}
                >
                  Approve
                </Button>
              )}
              {period.status === 'Approved' && (
                <Button
                  type="primary"
                  icon={<LockOutlined />}
                  loading={action === 'lock'}
                  onClick={() => run('lock', () => hrmsService.lockPayrollPeriod(id!), 'Locked')}
                >
                  Lock
                </Button>
              )}
              {(period.status === 'Approved' || period.status === 'Locked') && (
                <Button icon={<FileTextOutlined />} loading={action === 'gl'} onClick={glHandoff}>
                  {period.project_distribution_json?.gl_handoff_jv_id ? 'Recreate Finance JV' : 'Create Finance JV'}
                </Button>
              )}
              <Button
                icon={<FileExcelOutlined />}
                loading={action === 'register'}
                disabled={!rows.length}
                onClick={exportRegister}
              >
                Salary register (Excel)
              </Button>
              {!indiaPack && (
                <Button
                  icon={<PrinterOutlined />}
                  loading={action === 'labour-all'}
                  disabled={!rows.length}
                  onClick={() => printLabourCards()}
                >
                  Labour cards
                </Button>
              )}
              <Button
                icon={<PrinterOutlined />}
                loading={action === 'payslip-all'}
                disabled={!rows.length}
                onClick={() => printPayslips()}
              >
                Payslips
              </Button>
              {!indiaPack && (
                <Button icon={<DownloadOutlined />} loading={action === 'wps'} onClick={exportWps}>
                  WPS export
                </Button>
              )}
              {indiaPack && (
                <Button icon={<DownloadOutlined />} loading={action === 'neft'} onClick={exportNeft}>
                  NEFT export
                </Button>
              )}
              {canDelete && (
                <Button danger icon={<DeleteOutlined />} onClick={deleteRun}>
                  Delete run
                </Button>
              )}
            </Space>
          }
        />
      }
      workflow={<WorkflowStrip stages={workflow.stages} current={workflow.current} compact />}
    >
      <Row gutter={16}>
        <Col xs={24} lg={16}>
          {showRunCoverage && withoutTimesheet.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={`${withoutTimesheet.length} of ${period.employee_count ?? withoutTimesheet.length} employee(s) have no approved timesheet for this month`}
              description={
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Text type="secondary">
                    They are paid their full monthly salary and no Absent days are deducted. Approve their timesheets
                    (HR → Attendance → Monthly timesheets), then Recompute.
                  </Text>
                  <Table
                    size="small"
                    rowKey="employee_id"
                    pagination={withoutTimesheet.length > 10 ? { pageSize: 10, size: 'small' } : false}
                    dataSource={withoutTimesheet}
                    columns={[
                      {
                        title: 'Employee',
                        key: 'e',
                        render: (_, r) => `${r.employee_code} — ${r.full_name}`,
                      },
                      {
                        title: 'Timesheet',
                        dataIndex: 'timesheet_status',
                        width: 140,
                        render: (s: string) => (
                          <Tag color={s === 'None' ? 'default' : 'orange'}>{s === 'None' ? 'Not created' : s}</Tag>
                        ),
                      },
                      {
                        title: 'Net',
                        dataIndex: 'net',
                        width: 120,
                        align: 'right',
                      },
                    ]}
                  />
                  <Button size="small" onClick={() => navigate(timesheetsLink)}>
                    Open monthly timesheets
                  </Button>
                </Space>
              }
            />
          )}
          {showRunCoverage && notInRun.length > 0 && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={`${notInRun.length} active employee(s) are not in this run — no approved timesheet yet`}
              description={
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Text type="secondary">
                    Once their timesheets are approved, either Recompute this run (before submitting it) or open another
                    run for the same month — employees already paid in this run are skipped.
                  </Text>
                  <Table
                    size="small"
                    rowKey="employee_id"
                    pagination={notInRun.length > 10 ? { pageSize: 10, size: 'small' } : false}
                    dataSource={notInRun}
                    columns={[
                      {
                        title: 'Employee',
                        key: 'e',
                        render: (_, r) => `${r.employee_code} — ${r.full_name}`,
                      },
                      {
                        title: 'Timesheet',
                        dataIndex: 'timesheet_status',
                        width: 140,
                        render: (s: string) => (
                          <Tag color={s === 'None' ? 'default' : 'orange'}>{s === 'None' ? 'Not created' : s}</Tag>
                        ),
                      },
                    ]}
                  />
                  <Space>
                    <Button size="small" onClick={() => navigate(timesheetsLink)}>
                      Open monthly timesheets
                    </Button>
                    <Button size="small" onClick={() => navigate('/hr/payroll/periods/new')}>
                      Open another run
                    </Button>
                  </Space>
                </Space>
              }
            />
          )}
          {showRunCoverage && inOtherRuns > 0 && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={`${inOtherRuns} employee(s) skipped because they are already in another run for this month`}
            />
          )}
          <Card title="Summary" style={{ marginBottom: 16 }}>
            <Descriptions column={{ xs: 1, sm: 2 }} size="small">
              <Descriptions.Item label="Period">{periodLabel}</Descriptions.Item>
              <Descriptions.Item label="Run">
                {period.run_number} — {runScopeLabel}
              </Descriptions.Item>
              <Descriptions.Item label="Status">{period.status}</Descriptions.Item>
              <Descriptions.Item label="Department">
                {period.department_id
                  ? (() => {
                      const d = departments.find((x) => x.id === period.department_id);
                      return d ? `${d.department_code} - ${d.department_name}` : '—';
                    })()
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Employees">{period.employee_count ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Gross">{amt(period.total_gross)}</Descriptions.Item>
              <Descriptions.Item label="Deductions">{amt(period.total_deductions)}</Descriptions.Item>
              <Descriptions.Item label="Net">
                <Text strong>{amt(period.total_net)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Computed at">
                {register?.period.computed_at ? dayjs(register.period.computed_at).format('DD-MMM-YYYY HH:mm') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Finance JV">
                {gl?.journal_voucher_number || period.project_distribution_json?.gl_handoff_jv_number || '—'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {register && rows.length > 0 && (
            <Card
              title="Verification checks"
              extra={
                <Text type="secondary">
                  {flaggedCount} of {rows.length} employee(s) flagged
                </Text>
              }
              style={{ marginBottom: 16 }}
            >
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {register.checks.map((c) => (
                  <Alert
                    key={c.message}
                    type={c.level === 'error' ? 'error' : c.level === 'warning' ? 'warning' : 'success'}
                    showIcon
                    message={c.message}
                    description={c.employees?.length ? c.employees.join(' · ') : undefined}
                  />
                ))}
              </Space>
            </Card>
          )}
        </Col>
        <Col xs={24} lg={8}>
          {showApprovalPanel(period) && (
            <ApprovalPanel
              entityType={ENTITY_TYPE_HR_PAYROLL_PERIOD}
              entityId={period.id}
              extraInvalidateQueryKeys={[hrmsKeys.payrollPeriod(id!), hrmsKeys.payrollPeriods()]}
            />
          )}
        </Col>
      </Row>

      <Card
        title={`Salary register (${rows.length})`}
        extra={<Text type="secondary">Expand a row for the calculation of each component</Text>}
        style={{ marginBottom: 16 }}
      >
        <Table<PayrollRegisterRow>
          rowKey="entry_id"
          size="small"
          bordered
          loading={registerLoading}
          dataSource={rows}
          scroll={{ x: 'max-content' }}
          pagination={rows.length > 25 ? { pageSize: 25, size: 'small' } : false}
          locale={{
            emptyText: period.status === 'Draft' ? 'Compute the run to calculate salaries' : 'No employees',
          }}
          expandable={{
            rowExpandable: (r) => r.lines.length > 0,
            expandedRowRender: (r) => (
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Text type="secondary">
                  Monthly salary {amt(r.monthly_salary)} · Daily rate {amt(r.daily_rate)} (monthly ÷ 30) · Hourly{' '}
                  {amt(r.hourly_rate)} · {r.payment_method || 'No payment method'}
                  {r.bank_name ? ` · ${r.bank_name}` : ''}
                  {r.iban ? ` · ${r.iban}` : ''}
                </Text>
                <Table<PayrollRunLine>
                  size="small"
                  rowKey={(l) => `${l.type}-${l.code}-${l.name}-${l.amount}`}
                  pagination={false}
                  dataSource={r.lines}
                  columns={[
                    {
                      title: 'Component',
                      key: 'n',
                      render: (_, l) => `${l.code} — ${l.name}`,
                    },
                    {
                      title: 'Type',
                      dataIndex: 'type',
                      width: 110,
                      render: (t: string) => <Tag color={t === 'Deduction' ? 'red' : 'green'}>{t}</Tag>,
                    },
                    {
                      title: 'Calculation',
                      key: 'calc',
                      render: (_, l) => lineDetail(l),
                    },
                    {
                      title: 'Amount',
                      dataIndex: 'amount',
                      width: 130,
                      align: 'right',
                      render: (v: number, l) => (l.type === 'Deduction' ? `-${amt(v)}` : amt(v)),
                    },
                  ]}
                />
              </Space>
            ),
          }}
          columns={[
            {
              title: 'Employee',
              key: 'e',
              fixed: 'left',
              width: 220,
              render: (_, r) => (
                <Space direction="vertical" size={0}>
                  <Text strong>{r.full_name}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {r.employee_code}
                    {r.job_title ? ` · ${r.job_title}` : ''}
                  </Text>
                </Space>
              ),
            },
            {
              title: 'Timesheet',
              dataIndex: 'timesheet_status',
              width: 100,
              render: (s: string | null) => (
                <Tag color={s === 'Approved' ? 'green' : s ? 'orange' : 'default'}>{s || 'Not created'}</Tag>
              ),
            },
            {
              title: 'Days',
              children: [
                {
                  title: 'Paid',
                  key: 'pay',
                  width: 55,
                  align: 'right',
                  render: (_, r) => r.days.payable,
                },
                {
                  title: 'Pres.',
                  key: 'pre',
                  width: 55,
                  align: 'right',
                  render: (_, r) => r.days.present,
                },
                {
                  title: 'Abs.',
                  key: 'abs',
                  width: 55,
                  align: 'right',
                  render: (_, r) => r.days.absent || '',
                },
                {
                  title: 'Leave',
                  key: 'lv',
                  width: 55,
                  align: 'right',
                  render: (_, r) => r.days.leave || '',
                },
                {
                  title: 'Unpaid',
                  key: 'ul',
                  width: 60,
                  align: 'right',
                  render: (_, r) => r.days.unpaid_leave || '',
                },
                {
                  title: 'Hol.',
                  key: 'hol',
                  width: 50,
                  align: 'right',
                  render: (_, r) => r.days.holiday || '',
                },
                {
                  title: 'W/Off',
                  key: 'wo',
                  width: 55,
                  align: 'right',
                  render: (_, r) => r.days.weekly_off || '',
                },
              ],
            },
            {
              title: 'OT h',
              key: 'ot',
              width: 60,
              align: 'right',
              render: (_, r) => r.ot_hours || '',
            },
            {
              title: 'Monthly',
              key: 'm',
              width: 110,
              align: 'right',
              render: (_, r) => amt(r.monthly_salary),
            },
            ...componentColumns,
            {
              title: 'Gross',
              key: 'g',
              width: 115,
              align: 'right',
              render: (_, r) => amt(r.gross),
            },
            {
              title: 'Deductions',
              key: 'd',
              width: 110,
              align: 'right',
              render: (_, r) => amt(r.deductions),
            },
            {
              title: 'Net',
              key: 'n',
              width: 120,
              align: 'right',
              render: (_, r) => <Text strong>{amt(r.net)}</Text>,
            },
            {
              title: 'Checks',
              key: 'f',
              width: 220,
              render: (_, r) =>
                r.flags.length ? (
                  <Space direction="vertical" size={2}>
                    {r.flags.map((f) => (
                      <Tag key={f} color="orange" style={{ whiteSpace: 'normal' }}>
                        {f}
                      </Tag>
                    ))}
                  </Space>
                ) : (
                  <Tag color="green">OK</Tag>
                ),
            },
            {
              title: 'Print',
              key: 'p',
              fixed: 'right',
              width: 170,
              render: (_, r) => (
                <Space size={0}>
                  {!indiaPack && (
                    <Button
                      type="link"
                      size="small"
                      loading={action === `labour-${r.employee_id}`}
                      onClick={() => printLabourCards(r.employee_id)}
                    >
                      Labour card
                    </Button>
                  )}
                  <Button
                    type="link"
                    size="small"
                    loading={action === `payslip-${r.employee_id}`}
                    onClick={() => printPayslips(r.employee_id)}
                  >
                    Payslip
                  </Button>
                </Space>
              ),
            },
          ]}
          summary={() =>
            register && rows.length > 0 ? (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={11}>
                    <Text strong>Total — {register.totals.employees} employee(s)</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={11} align="right">
                    {amt(rows.reduce((s, r) => s + r.monthly_salary, 0))}
                  </Table.Summary.Cell>
                  {register.columns.map((c, i) => (
                    <Table.Summary.Cell key={`${c.type}:${c.code}`} index={12 + i} align="right">
                      {amt(register.totals.by_code[`${c.type}:${c.code}`] || 0)}
                    </Table.Summary.Cell>
                  ))}
                  <Table.Summary.Cell index={12 + register.columns.length} align="right">
                    <Text strong>{amt(register.totals.gross)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={13 + register.columns.length} align="right">
                    <Text strong>{amt(register.totals.deductions)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={14 + register.columns.length} align="right">
                    <Text strong>{amt(register.totals.net)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={15 + register.columns.length} colSpan={2} />
                </Table.Summary.Row>
              </Table.Summary>
            ) : null
          }
        />
      </Card>

      {gl && rows.length > 0 && (
        <Card
          title="Finance JV preview"
          extra={
            <Space>
              <Tag color={gl.balanced ? 'green' : 'red'}>{gl.balanced ? 'Balanced' : 'Not balanced'}</Tag>
              {gl.journal_voucher_number && <Tag color="blue">Draft JV {gl.journal_voucher_number}</Tag>}
            </Space>
          }
          style={{ marginBottom: 16 }}
        >
          {gl.issues.length > 0 && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message="Map these accounts before creating the Finance JV"
              description={
                <Space direction="vertical" size={2}>
                  {gl.issues.map((i) => (
                    <Text key={i}>• {i}</Text>
                  ))}
                  <Text type="secondary">
                    Expense accounts per head: HR → Salary heads. Default expense, payable and loan accounts: HR → HRMS
                    setup.
                  </Text>
                </Space>
              }
            />
          )}
          <Table<PayrollGlLine>
            size="small"
            rowKey={(l) => `${l.account_role}-${l.account_id}-${l.description}`}
            pagination={false}
            dataSource={gl.lines}
            columns={[
              {
                title: 'Account',
                key: 'a',
                render: (_, l) =>
                  l.account_id ? (
                    `${l.account_code || ''} — ${l.account_name || ''}`
                  ) : (
                    <Tag color="red">Not mapped</Tag>
                  ),
              },
              {
                title: 'Role',
                key: 'r',
                width: 150,
                render: (_, l) => GL_ROLE_LABEL[l.account_role],
              },
              { title: 'Description', dataIndex: 'description' },
              {
                title: 'Debit',
                dataIndex: 'debit_amount',
                width: 130,
                align: 'right',
                render: (v: number) => (v ? amt(v) : ''),
              },
              {
                title: 'Credit',
                dataIndex: 'credit_amount',
                width: 130,
                align: 'right',
                render: (v: number) => (v ? amt(v) : ''),
              },
            ]}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <Text strong>Total</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <Text strong>{amt(gl.total_debit)}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  <Text strong>{amt(gl.total_credit)}</Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        </Card>
      )}

      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <Card title="Payment batches" style={{ marginBottom: 16 }}>
            <Table
              rowKey="id"
              size="small"
              loading={payLoading}
              dataSource={payments}
              pagination={false}
              columns={[
                { title: 'Batch #', dataIndex: 'payment_batch_number' },
                {
                  title: 'Date',
                  dataIndex: 'payment_date',
                  render: (d: string) => (d ? dayjs(d).format('DD-MMM-YYYY') : '—'),
                },
                { title: 'Method', dataIndex: 'payment_method', width: 120 },
                { title: 'Amount', dataIndex: 'total_amount', width: 110 },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  width: 110,
                  render: (s: string) => (
                    <Tag color={s === 'Paid' ? 'green' : s === 'Draft' ? 'default' : 'blue'}>{s}</Tag>
                  ),
                },
                {
                  title: 'Paid at',
                  dataIndex: 'paid_at',
                  width: 140,
                  render: (d: string) => (d ? dayjs(d).format('DD-MMM-YYYY HH:mm') : '—'),
                },
                {
                  title: 'Actions',
                  key: 'actions',
                  width: 190,
                  render: (_, r) =>
                    (r.status === 'Draft' || r.status === 'Pending') && (
                      <Space size={0}>
                        <Button
                          type="link"
                          size="small"
                          icon={<CheckOutlined />}
                          loading={action === `paid-${r.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            markPaid(r.id);
                          }}
                        >
                          Mark paid
                        </Button>
                        {r.status === 'Draft' && (
                          <Button
                            type="link"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              deletePayment(r);
                            }}
                          >
                            Delete
                          </Button>
                        )}
                      </Space>
                    ),
                },
              ]}
            />
            <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              Net pay {amt(period.total_net)} · In batches {amt(batchedAmount)} · Unpaid balance{' '}
              {amt(Math.max(unpaidBalance, 0))}
            </Text>
            {canCreateBatch ? (
              <Card type="inner" title="Create payment batch" size="small" style={{ marginTop: 12 }}>
                <Form
                  form={payForm}
                  layout="inline"
                  initialValues={{
                    payment_method: 'BankTransfer',
                    payment_date: dayjs(),
                  }}
                  onFinish={createPayment}
                >
                  <Form.Item name="payment_date" rules={[{ required: true }]}>
                    <DatePicker />
                  </Form.Item>
                  <Form.Item name="payment_method">
                    <Select
                      style={{ width: 140 }}
                      options={(indiaPack
                        ? ['BankTransfer', 'NEFT', 'Cash', 'Cheque']
                        : ['BankTransfer', 'Cash', 'Cheque', 'WPS']
                      ).map((v) => ({ value: v, label: v }))}
                    />
                  </Form.Item>
                  <Form.Item name="total_amount">
                    <InputNumber
                      min={0.01}
                      max={unpaidBalance}
                      placeholder={`Amount (${amt(unpaidBalance)})`}
                      style={{ width: 170 }}
                    />
                  </Form.Item>
                  <Form.Item name="notes">
                    <Input placeholder="Notes" style={{ width: 160 }} />
                  </Form.Item>
                  <Button type="primary" htmlType="submit">
                    Create batch
                  </Button>
                </Form>
              </Card>
            ) : (
              <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                {unpaidBalance > 0
                  ? 'Payment batches can be created once the run is approved.'
                  : 'The full net pay is covered by payment batches.'}
              </Text>
            )}
          </Card>

          <EntityAttachmentsCard entityType="HR_PAYROLL_PERIOD" entityId={period.id} />
        </Col>
      </Row>
    </WorkspaceLayout>
  );
};

export default PayrollPeriodDetailPage;
